import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import env from 'config/env';
import { Buffer } from 'buffer';
import CryptoJS from 'crypto-js';
import * as bip39 from 'bip39';

// Add this line after the imports
window.Buffer = Buffer;

interface User {
  id: string;
  email: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<{ recoveryPhrase: string }>;
  logout: () => void;
  deriveMasterKey: (password: string) => string;
  deriveMasterKeyFromRecovery: (phrase: string) => string;
  encryptMasterKey: (masterKey: string, password: string) => string;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  recoverAccount: (email: string, recoveryPhrase: string, newPassword: string) => Promise<void>;
  verifyRecoveryPhrase: (phrase: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const generateRecoveryPhrase = (): string => {
    // Generate a random 128-bit entropy
    const entropy = CryptoJS.lib.WordArray.random(16);
    // Convert to hex string
    const entropyHex = entropy.toString(CryptoJS.enc.Hex);
    // Generate mnemonic from entropy
    return bip39.entropyToMnemonic(entropyHex);
  };

  const verifyRecoveryPhrase = (phrase: string): boolean => {
    return bip39.validateMnemonic(phrase);
  };

  const deriveMasterKeyFromRecovery = (phrase: string): string => {
    if (!verifyRecoveryPhrase(phrase)) {
      throw new Error('Invalid recovery phrase');
    }
    // Convert mnemonic to seed
    const seed = bip39.mnemonicToSeedSync(phrase);
    // Use the first 32 bytes of the seed as our master key
    const masterKey = CryptoJS.lib.WordArray.create(seed.slice(0, 32));
    return masterKey.toString();
  };

  const deriveMasterKey = (password: string): string => {
    // Use PBKDF2 to derive a secure key from the password
    const salt = 'pragma_journal_salt';
    const key = CryptoJS.PBKDF2(password, salt, {
      keySize: 256/32,
      iterations: 10000
    });
    return key.toString();
  };

  const encryptMasterKey = (masterKey: string, password: string): string => {
    return CryptoJS.AES.encrypt(masterKey, password).toString();
  };

  const decryptMasterKey = (encryptedMasterKey: string, password: string): string => {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedMasterKey, password);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      throw new Error('Failed to decrypt master key');
    }
  };

  const register = async (email: string, password: string) => {
    try {
      // Generate recovery phrase
      const recoveryPhrase = generateRecoveryPhrase();
      
      // Derive master key from recovery phrase
      const masterKey = deriveMasterKeyFromRecovery(recoveryPhrase);
      
      // Encrypt master key with password for database storage
      const encryptedMasterKey = encryptMasterKey(masterKey, password);

      // Create verification data
      const verificationString = CryptoJS.lib.WordArray.random(32).toString();
      const encryptedVerification = CryptoJS.AES.encrypt(verificationString, masterKey).toString();
      
      await axios.post(`${env.API_URL}/auth/register`, {
        email,
        password,
        encryptedMasterKey,
        encryptedVerification,
        verificationString
      });
      
      // After registration, log the user in
      await login(email, password);
      
      // Store master key in session storage
      localStorage.setItem('masterKey', masterKey);
      
      // Return recovery phrase to be shown to user
      return { recoveryPhrase };
    } catch (error) {
      console.error('Registration error:', error);
      throw new Error('Registration failed');
    }
  };

  const recoverAccount = async (email: string, recoveryPhrase: string, newPassword: string) => {
    try {
      if (!verifyRecoveryPhrase(recoveryPhrase)) {
        throw new Error('Invalid recovery phrase');
      }

      // Derive master key from recovery phrase
      const masterKey = deriveMasterKeyFromRecovery(recoveryPhrase);
      
      // Encrypt master key with new password
      const newEncryptedMasterKey = encryptMasterKey(masterKey, newPassword);

      // Request password reset with recovery
      await axios.post(`${env.API_URL}/auth/recover`, {
        email,
        newPassword,
        encryptedMasterKey: newEncryptedMasterKey
      });

      // Log in with new password
      await login(email, newPassword);

    } catch (error) {
      console.error('Recovery error:', error);
      throw new Error('Account recovery failed');
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      // First verify the current password with the server
      const response = await axios.post(`${env.API_URL}/auth/verify-password`, {
        password: currentPassword
      });

      if (!response.data.valid) {
        throw new Error('Current password is incorrect');
      }

      // Get the current master key
      const masterKey = localStorage.getItem('masterKey');
      if (!masterKey) {
        throw new Error('Master key not found');
      }

      // Re-encrypt master key with new password
      const newEncryptedMasterKey = encryptMasterKey(masterKey, newPassword);

      // Update the password on the server
      await axios.put(`${env.API_URL}/auth/password`, {
        currentPassword,
        newPassword,
        newEncryptedMasterKey
      });

    } catch (error) {
      console.error('Password change error:', error);
      throw new Error('Failed to change password');
    }
  };

  useEffect(() => {
    const validateToken = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get(`${env.API_URL}/auth/validate`);
        
        setUser({
          id: response.data.id,
          email: response.data.email
        });

        if (response.data.accessToken) {
          localStorage.setItem('authToken', response.data.accessToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
        }
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('masterKey');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await axios.post(`${env.API_URL}/auth/login`, { email, password });
      const { accessToken, user: userData } = response.data;
      
      const masterKey = decryptMasterKey(userData.encryptedMasterKey, password);
      localStorage.setItem('masterKey', masterKey);
      
      localStorage.setItem('authToken', accessToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      
      setUser({
        id: userData.id,
        email
      });
    } catch (error) {
      console.error('Login error:', error);
      throw new Error('Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('masterKey');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading,
      login, 
      register, 
      logout, 
      deriveMasterKey,
      deriveMasterKeyFromRecovery,
      encryptMasterKey,
      changePassword,
      recoverAccount,
      verifyRecoveryPhrase
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 